import { useEffect, useState } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const TABS = ["Billing History", "Wallet Transactions", "Loyalty Transactions"];

const Badge = ({ type }) => {
  const map = {
    CREDIT:  { bg: "bg-green-100 text-green-700",  label: "Credit" },
    DEBIT:   { bg: "bg-red-100 text-red-700",      label: "Debit" },
    EARNED:  { bg: "bg-blue-100 text-blue-700",    label: "Earned" },
    REDEEMED:{ bg: "bg-amber-100 text-amber-700",  label: "Redeemed" },
    EXPIRED: { bg: "bg-gray-100 text-gray-600",    label: "Expired" },
    ADJUSTED:{ bg: "bg-purple-100 text-purple-700",label: "Adjusted" },
  };
  const style = map[type] || { bg: "bg-gray-100 text-gray-600", label: type };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.bg}`}>
      {style.label}
    </span>
  );
};

const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
    <span className="text-5xl mb-3">{icon}</span>
    <p className="text-sm">{text}</p>
  </div>
);

const CustomerTransactionModal = ({ customer, onClose }) => {
  const [tab, setTab] = useState(0);
  const [bills, setBills] = useState([]);
  const [walletTxns, setWalletTxns] = useState([]);
  const [loyaltyTxns, setLoyaltyTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [billsRes, txnRes] = await Promise.all([
          api.get(`/customers/${customer._id}/bills?page=1&limit=50`),
          api.get(`/customers/${customer._id}/transactions`),
        ]);
        setBills(billsRes.data.data || []);
        setWalletTxns(txnRes.data.wallet || []);
        setLoyaltyTxns(txnRes.data.loyalty || []);
      } catch {
        toast.error("Failed to load transaction history");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customer._id]);

  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);

  const fmtDate = (d) =>
    new Date(d).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {customer.name}
            </h2>
            <p className="text-sm text-gray-500">{customer.mobile}</p>
          </div>

          {/* Summary Chips */}
          <div className="flex gap-3 items-center flex-wrap">
            <div className="text-center bg-indigo-50 rounded-xl px-4 py-2">
              <p className="text-xs text-indigo-500 font-medium">🌟 Points</p>
              <p className="text-lg font-bold text-indigo-700">{customer.loyaltyPoints || 0}</p>
            </div>
            <div className="text-center bg-emerald-50 rounded-xl px-4 py-2">
              <p className="text-xs text-emerald-500 font-medium">👛 Wallet</p>
              <p className="text-lg font-bold text-emerald-700">₹{fmt(customer.walletBalance)}</p>
            </div>
            <div className="text-center bg-gray-50 rounded-xl px-4 py-2">
              <p className="text-xs text-gray-500 font-medium">🧾 Bills</p>
              <p className="text-lg font-bold text-gray-700">{customer.totalBills || 0}</p>
            </div>
            <button
              onClick={onClose}
              className="ml-2 text-gray-400 hover:text-gray-700 text-2xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === i
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Tab 0: Billing History ── */}
              {tab === 0 && (
                bills.length === 0 ? (
                  <EmptyState icon="🧾" text="No billing history yet" />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs uppercase bg-gray-50 rounded">
                        <th className="p-3">Bill No</th>
                        <th className="p-3">Date</th>
                        <th className="p-3 text-right">Amount</th>
                        <th className="p-3 text-right">Pts Earned</th>
                        <th className="p-3 text-right">Pts Used</th>
                        <th className="p-3 text-right">Wallet Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bills.map((b) => (
                        <tr key={b._id} className="border-t hover:bg-gray-50">
                          <td className="p-3 font-medium text-blue-600">{b.billId}</td>
                          <td className="p-3 text-gray-500">{fmtDate(b.billDate)}</td>
                          <td className="p-3 text-right font-semibold text-green-700">₹{fmt(b.totalAmount)}</td>
                          <td className="p-3 text-right text-indigo-600 font-medium">
                            {b.loyaltyPointsEarned > 0 ? `+${b.loyaltyPointsEarned}` : "-"}
                          </td>
                          <td className="p-3 text-right text-amber-600 font-medium">
                            {b.loyaltyPointsRedeemed > 0 ? `-${b.loyaltyPointsRedeemed}` : "-"}
                          </td>
                          <td className="p-3 text-right text-emerald-600 font-medium">
                            {b.walletAmountRedeemed > 0 ? `₹${fmt(b.walletAmountRedeemed)}` : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* ── Tab 1: Wallet Transactions ── */}
              {tab === 1 && (
                walletTxns.length === 0 ? (
                  <EmptyState icon="👛" text="No wallet transactions yet" />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs uppercase bg-gray-50 rounded">
                        <th className="p-3">Date</th>
                        <th className="p-3">Type</th>
                        <th className="p-3 text-right">Amount</th>
                        <th className="p-3">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTxns.map((t) => (
                        <tr key={t._id} className="border-t hover:bg-gray-50">
                          <td className="p-3 text-gray-500 text-xs">{fmtDate(t.createdAt)}</td>
                          <td className="p-3"><Badge type={t.type} /></td>
                          <td className={`p-3 text-right font-semibold ${t.type === "CREDIT" ? "text-green-600" : "text-red-600"}`}>
                            {t.type === "CREDIT" ? "+" : ""}₹{fmt(Math.abs(t.amount))}
                          </td>
                          <td className="p-3 text-gray-500 text-xs">{t.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* ── Tab 2: Loyalty Transactions ── */}
              {tab === 2 && (
                loyaltyTxns.length === 0 ? (
                  <EmptyState icon="⭐" text="No loyalty transactions yet" />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs uppercase bg-gray-50 rounded">
                        <th className="p-3">Date</th>
                        <th className="p-3">Type</th>
                        <th className="p-3 text-right">Points</th>
                        <th className="p-3">Reason</th>
                        <th className="p-3 text-center">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loyaltyTxns.map((t) => (
                        <tr key={t._id} className="border-t hover:bg-gray-50">
                          <td className="p-3 text-gray-500 text-xs">{fmtDate(t.createdAt)}</td>
                          <td className="p-3"><Badge type={t.type} /></td>
                          <td className={`p-3 text-right font-semibold ${t.points > 0 ? "text-blue-600" : "text-red-500"}`}>
                            {t.points > 0 ? `+${t.points}` : t.points}
                          </td>
                          <td className="p-3 text-gray-500 text-xs">{t.reason || "—"}</td>
                          <td className="p-3 text-center text-xs">
                            {t.expiryDate
                              ? new Date(t.expiryDate) < new Date()
                                ? <span className="text-red-500 font-medium">Expired</span>
                                : <span className="text-amber-600">{new Date(t.expiryDate).toLocaleDateString()}</span>
                              : <span className="text-green-600">♾️ Never</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerTransactionModal;
